import React from 'react'
import styled from 'styled-components'
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';

const Card = styled.div`
    width: 100%;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.card_border};
    backdrop-filter: blur(10px);
    cursor: pointer;
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    transition: all 0.35s ease;
    min-height: 220px;

    &:hover {
        border-color: ${({ theme }) => theme.primary}55;
        box-shadow: 0 12px 48px ${({ theme }) => theme.shadow_primary};
        transform: translateY(-4px);
    }

    @media (max-width: 768px) {
        flex-direction: column;
        min-height: unset;
    }
`

const ImageWrapper = styled.div`
    width: 300px;
    min-width: 300px;
    overflow: hidden;
    position: relative;
    flex-shrink: 0;

    @media (max-width: 960px) {
        width: 240px;
        min-width: 240px;
    }

    @media (max-width: 768px) {
        width: 100%;
        min-width: unset;
        height: 150px;
    }
`

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;

    ${Card}:hover & {
        transform: scale(1.06);
    }
`

const CategoryBadge = styled.div`
    position: absolute;
    top: 12px;
    left: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${({ theme }) => theme.white};
    background: ${({ theme }) => theme.gradient_primary};
    padding: 4px 10px;
    border-radius: 20px;
`

const Content = styled.div`
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    justify-content: space-between;

    @media (max-width: 768px) {
        padding: 14px 16px;
        gap: 8px;
    }
`

const Top = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const Title = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: ${({ theme }) => theme.text_primary};
    letter-spacing: -0.3px;
    line-height: 1.3;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`

const Description = styled.div`
    font-weight: 400;
    font-size: 14px;
    line-height: 1.7;
    color: ${({ theme }) => theme.text_secondary};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 768px) {
        font-size: 13px;
        line-height: 1.55;
        -webkit-line-clamp: 2;
    }
`

const Bottom = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
`

const Tags = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`

const Tag = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.primary_light};
    padding: 3px 10px;
    border-radius: 20px;
    letter-spacing: 0.2px;

    @media (max-width: 768px) {
        font-size: 10px;
        padding: 2px 8px;
    }
`

const Actions = styled.div`
    display: flex;
    gap: 10px;
    flex-shrink: 0;
`

const ActionBtn = styled.a`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 8px;

    @media (max-width: 768px) {
        font-size: 11px;
        padding: 5px 11px;
    }
    text-decoration: none;
    transition: all 0.25s ease;
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.divider};
    color: ${({ theme }) => theme.text_secondary};
    background: transparent;

    &:hover {
        border-color: ${({ theme }) => theme.primary};
        color: ${({ theme }) => theme.primary};
        background: ${({ theme }) => theme.primary_light};
    }

    ${({ $primary, theme }) => $primary && `
        background: ${theme.gradient_primary};
        color: #fff;
        border-color: transparent;
        &:hover {
            opacity: 0.9;
            color: #fff;
            background: ${theme.gradient_primary};
            border-color: transparent;
        }
    `}
`

const ProjectCards = ({ project, setOpenModal }) => {
    const handleCardClick = () => {
        setOpenModal({ state: true, project: project });
    };

    const handleActionClick = (e) => {
        e.stopPropagation();
    };

    return (
        <Card onClick={handleCardClick}>
            <ImageWrapper>
                <Image src={project.image} alt={project.title} />
                <CategoryBadge>{project.category}</CategoryBadge>
            </ImageWrapper>
            <Content>
                <Top>
                    <Title>{project.title}</Title>
                    <Description>{project.description}</Description>
                </Top>
                <Bottom>
                    <Tags>
                        {project.tags?.slice(0, 4).map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                        ))}
                        {project.tags?.length > 4 && (
                            <Tag>+{project.tags.length - 4}</Tag>
                        )}
                    </Tags>
                    <Actions onClick={handleActionClick}>
                        {project.github && (
                            <ActionBtn href={project.github} target="_blank" rel="noopener noreferrer">
                                <FaGithub size={13} /> Code
                            </ActionBtn>
                        )}
                        {project.webapp && !project.webapp.includes('add your') && (
                            <ActionBtn href={project.webapp} target="_blank" rel="noopener noreferrer" $primary>
                                <FaExternalLinkAlt size={11} /> Live
                            </ActionBtn>
                        )}
                    </Actions>
                </Bottom>
            </Content>
        </Card>
    )
}

export default ProjectCards
